import { Controller } from '@hotwired/stimulus'

// Embeds Melio's web app in one visible iframe, per the "Partial iframe"
// integration in Melio's integration guide. Melio's own screens (missing
// account info, terms, onboarding, the payments dashboard) all render here.
//
// We don't use @melio-eng/web-sdk: 1.2.0 signs in through a hidden 1px
// iframe, so any screen Melio shows during sign-in (e.g. collecting a missing
// company address) is invisible and the page hangs.
const DASHBOARD = '/pay-dashboard/payments'
const ENTRYPOINT = 'bk360-bill-pay'
const RELOAD_KEY = 'billPay.sessionExpiredReloadAt'
const RELOAD_COOLDOWN_MS = 60_000
// Mirrors BillPay::EmbedTarget::PATTERN, so a synced URL reloads to the same page.
const TARGET_PATTERN = /^\/(?!\/)[A-Za-z0-9\-_/.,=&?]{0,499}$/

export default class extends Controller {
  static values = {
    authCode: String,
    partnerName: String,
    origin: String,
    eventsUrl: String,
    target: String,
  }

  connect() {
    this.onMessage = (event) => this.handleMessage(event)
    window.addEventListener('message', this.onMessage)

    this.iframe = this.buildIframe()
    this.element.appendChild(this.iframe)
    this.watchLayout()
    this.navigate(this.startLocation())
  }

  disconnect() {
    window.removeEventListener('message', this.onMessage)
    this.unwatchLayout()
    this.iframe?.remove()
  }

  // The iframe fills the page under our top bar and scrolls itself, so Melio
  // only needs to know where it sits and how big it is (guide: "Synchronizing
  // frame dimensions and position").
  watchLayout() {
    this.onResize = () => this.sendDimensions()

    window.addEventListener('resize', this.onResize)
    window.visualViewport?.addEventListener('resize', this.onResize)
    this.layoutObserver = new ResizeObserver(this.onResize)
    // Toggling the sidebar moves the iframe without resizing the window.
    this.layoutObserver.observe(this.element)
  }

  unwatchLayout() {
    window.removeEventListener('resize', this.onResize)
    window.visualViewport?.removeEventListener('resize', this.onResize)
    this.layoutObserver?.disconnect()
  }

  buildIframe() {
    const iframe = document.createElement('iframe')
    iframe.title = 'Melio Bill Pay'
    iframe.allow = 'clipboard-write'
    Object.assign(iframe.style, { display: 'block', flex: '1 1 auto', width: '100%', border: '0' })
    return iframe
  }

  // Reusing the same token in the same browser session doesn't trigger a new
  // token exchange (guide: "Reusing token in session"), so switching between
  // onboarding and the dashboard is safe with our single-use code.
  //
  // `target` takes a page from the guide's Navigation Targets (/view-vendors);
  // `redirectUrl` takes one of Melio's internal routes (dashboard, onboarding).
  //
  // After the first load the frame is moved with location.replace, so
  // switching to or from onboarding adds no entry to the browser history.
  navigate(location) {
    this.location = location
    const params = new URLSearchParams({ token: this.authCodeValue, ...location, entrypoint: ENTRYPOINT })
    const url = `${this.originValue}/${encodeURIComponent(this.partnerNameValue)}/auth?${params}`

    if (this.iframe.src) {
      this.iframe.contentWindow.location.replace(url)
    } else {
      this.iframe.src = url
    }
  }

  handleMessage(event) {
    if (event.origin !== this.originValue || event.source !== this.iframe?.contentWindow) return

    const message = this.parse(event.data)
    switch (message?.type) {
      case 'NAVIGATED_TO_TARGET':
        this.syncUrl(message.target)
        if (message.targetAction === 'schedulePaymentSuccess') this.report('payment_scheduled', message)
        break
      case 'LOADED':
        this.lastDimensions = null
        this.sendDimensions()
        break
      case 'ONBOARDING_REQUIRED':
        this.navigateOnce(this.onboardingTarget())
        break
      case 'ONBOARDING_FORM_COMPLETED':
        this.navigateOnce(this.startLocation())
        break
      case 'SESSION_EXPIRED':
        this.reloadForFreshCode()
        break
      case 'AUTHENTICATION_ERROR':
        this.showError('Authentication failed. Refresh the page to try again.', message.requestId)
        break
    }
  }

  // Melio sends SDK partners no webhooks, so the page reports what it sees.
  report(kind, message) {
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    fetch(this.eventsUrlValue, {
      method: 'POST',
      keepalive: true,
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-CSRF-Token': token },
      body: JSON.stringify({
        event: { kind, melio_message_id: message.messageId, data: { target: message.target, target_action: message.targetAction } },
      }),
    }).catch(() => {})
  }

  send(type, payload) {
    const message = JSON.stringify({ type, messageId: crypto.randomUUID(), ...payload })
    this.iframe?.contentWindow?.postMessage(message, this.originValue)
  }

  sendDimensions() {
    if (!this.iframe) return

    const bounds = this.iframe.getBoundingClientRect()
    const dimensions = {
      windowHeight: window.innerHeight,
      windowWidth: window.innerWidth,
      elementDistanceFromTop: bounds.top,
      elementDistanceFromLeft: bounds.left,
      visualViewportHeight: window.visualViewport?.height,
      visualViewportWidth: window.visualViewport?.width,
    }
    const key = JSON.stringify(dimensions)
    if (key === this.lastDimensions) return

    this.lastDimensions = key
    this.send('DIMENSIONS_CHANGED', dimensions)
  }

  navigateOnce(location) {
    if (JSON.stringify(this.location) !== JSON.stringify(location)) this.navigate(location)
  }

  startLocation() {
    return this.targetValue ? { target: this.targetValue } : { redirectUrl: DASHBOARD }
  }

  onboardingTarget() {
    return { redirectUrl: `/welcome?externalOrigin=${encodeURIComponent(this.partnerNameValue)}` }
  }

  // Mirrors the Melio page in our URL so it can be bookmarked and reloaded
  // (guide: "Synchronizing urls"). replaceState, because Melio already pushed
  // its own history entry for this navigation. Slashes are left unencoded.
  syncUrl(target) {
    if (typeof target !== 'string' || !TARGET_PATTERN.test(target)) return

    const query = `target=${encodeURIComponent(target).replaceAll('%2F', '/')}`
    history.replaceState(history.state, '', `${window.location.pathname}?${query}`)
  }

  // A reload goes back through the Bill Pay gate and mints a fresh code. If
  // Melio expires that one too within a minute, stop instead of looping.
  reloadForFreshCode() {
    const now = Date.now()
    let lastReload = 0
    try {
      lastReload = Number(sessionStorage.getItem(RELOAD_KEY)) || 0
      sessionStorage.setItem(RELOAD_KEY, String(now))
    } catch {
      // Storage unavailable (private mode, blocked site data): fall through and reload.
    }

    if (now - lastReload < RELOAD_COOLDOWN_MS) {
      this.showError('Your Bill Pay session ended. Refresh the page to start a new one.')
    } else {
      window.location.reload()
    }
  }

  parse(data) {
    if (typeof data !== 'string') return data

    try {
      return JSON.parse(data)
    } catch {
      return null
    }
  }

  showError(text, requestId) {
    this.iframe?.remove()
    this.iframe = null

    const alert = document.createElement('div')
    alert.className = 'alert alert-warning m-3'
    alert.setAttribute('role', 'alert')
    alert.textContent = text
    if (requestId) {
      const reference = document.createElement('div')
      reference.className = 'small text-body-secondary mt-1'
      reference.textContent = `Reference: ${requestId}`
      alert.appendChild(reference)
    }
    this.element.appendChild(alert)
  }
}
